import { useDecimalOperator } from "@/hooks/use-decimal-operator";
import { cn } from "@/lib/utils";

export const InputWithInfo = ({
    label,
    error = false,
    disabled = false,
    onChange,
    value,
    sign = "+",
    infoLeft,
    infoRight,
    adornment,
    inputRef,
}: {
    label: string;
    error?: boolean;
    disabled?: boolean;
    onChange?: (value: string) => void;
    value?: string;
    sign?: "+" | "-";
    infoLeft?: React.ReactNode;
    infoRight?: React.ReactNode;
    adornment?: React.ReactNode;
    inputRef?: React.Ref<HTMLInputElement>;
}) => {
    const separator = useDecimalOperator();

    return (
        <div
            className={cn(
                "flex w-full flex-col rounded-md border p-3 transition-all",
                "focus-within:outline-none focus-within:outline-hidden focus-within:ring-1 focus-within:ring-ring",
                error ? "border-error" : "border-input",
            )}
        >
            <div className="flex w-full justify-between">
                <div className="font-bold">{label}</div>
                <div className="flex basis-1/3">
                    <input
                        ref={inputRef}
                        value={value}
                        inputMode="decimal"
                        disabled={disabled}
                        onFocus={(e) => {
                            e.currentTarget.setSelectionRange(
                                e.currentTarget.value.length,
                                e.currentTarget.value.length,
                            );
                        }}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            // Remove any character that is not a digit, comma, or period
                            let cleanInput = inputValue.replace(/[^0-9,.]/g, "");
                            // Keep only the first separator (comma or period)
                            let firstSeparatorFound = false;
                            cleanInput = cleanInput.replace(/[,.]/g, (match) => {
                                if (firstSeparatorFound) return ""; // If a separator was found before, remove this one
                                firstSeparatorFound = true; // If this is the first separator, keep it
                                return match;
                            });

                            // Replace , and . with local separator
                            cleanInput = cleanInput.replace(/[,.]/g, separator);

                            onChange?.(cleanInput === "" ? cleanInput : `${sign} ${cleanInput}`);
                        }}
                        placeholder={`${sign} 0.0`}
                        className={cn(
                            "w-full bg-transparent text-right placeholder:text-muted-foreground focus:outline-none focus:ring-0",
                            disabled && "cursor-not-allowed opacity-50",
                        )}
                    />
                    {adornment != null && <div className="ml-2 text-right font-bold">{adornment}</div>}
                </div>
            </div>
            <div>
                <div className="flex justify-between text-xs">
                    <div className={cn(error ? "text-error" : "text-muted-foreground")}>{infoLeft}</div>
                    <div className={cn(error ? "text-error" : "text-muted-foreground")}>{infoRight}</div>
                </div>
            </div>
        </div>
    );
};
